import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import AddGuestModal from '../../guest-management/components/AddGuestModal';

const GuestSelectionPanel = ({ selectedGuests, onGuestToggle, onFamilyToggle }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showFamilyGroups, setShowFamilyGroups] = useState(false);
  const [isAddGuestModalOpen, setIsAddGuestModalOpen] = useState(false);
  const [showGiftChecklist, setShowGiftChecklist] = useState(false);

  // Mock guest data with enhanced gift and preference information
  const mockGuests = [
  {
    id: 1,
    name: "Sarah Johnson",
    email: "sarah.johnson@email.com",
    avatar: "https://images.unsplash.com/photo-1730222168387-051038de25be",
    avatarAlt: "Professional woman with shoulder-length brown hair smiling at camera",
    familyId: "family_1",
    familyName: "Johnson Family",
    dietaryRestrictions: ["Vegetarian", "Gluten-Free"],
    allergies: ["Nuts"],
    preferences: ["Italian", "Mediterranean"],
    lastVisit: "2024-09-15",
    // New gift management fields
    bringsGiftsForKids: true,
    preferredGiftTypes: ["books", "educational"],
    giftBudgetRange: "25_50",
    giftNotes: "Usually brings educational books for ages 6-12",
    // New preference fields
    winePreferences: "Prefers white wine, especially Sauvignon Blanc",
    foodPreferences: "Loves Mediterranean cuisine, enjoys fresh herbs and olive oil dishes"
  },
  {
    id: 2,
    name: "Michael Johnson",
    email: "michael.johnson@email.com",
    avatar: "https://images.unsplash.com/photo-1705645930353-0e335311ef20",
    avatarAlt: "Professional man with short dark hair in blue shirt smiling",
    familyId: "family_1",
    familyName: "Johnson Family",
    dietaryRestrictions: [],
    allergies: ["Shellfish"],
    preferences: ["BBQ", "American"],
    lastVisit: "2024-09-15",
    bringsGiftsForKids: true,
    preferredGiftTypes: ["toys", "outdoor"],
    giftBudgetRange: "50_100",
    giftNotes: "Loves getting sports equipment and outdoor games",
    winePreferences: "Enjoys red wine, particularly Cabernet Sauvignon",
    foodPreferences: "Big fan of grilled meats and hearty American dishes"
  },
  {
    id: 3,
    name: "Emma Rodriguez",
    email: "emma.rodriguez@email.com",
    avatar: "https://images.unsplash.com/photo-1597725096039-7a277e984835",
    avatarAlt: "Young Hispanic woman with long dark hair and bright smile",
    familyId: null,
    dietaryRestrictions: ["Vegan"],
    allergies: [],
    preferences: ["Mexican", "Asian"],
    lastVisit: "2024-10-01",
    bringsGiftsForKids: false,
    preferredGiftTypes: [],
    giftBudgetRange: "",
    giftNotes: "",
    winePreferences: "Prefers organic wines, sometimes brings homemade agua frescas",
    foodPreferences: "Passionate about plant-based Mexican cuisine and Asian fusion"
  },
  {
    id: 4,
    name: "David Chen",
    email: "david.chen@email.com",
    avatar: "https://images.unsplash.com/photo-1558356811-8e77884f44d3",
    avatarAlt: "Asian man with glasses and professional attire smiling warmly",
    familyId: "family_2",
    familyName: "Chen Family",
    dietaryRestrictions: [],
    allergies: ["Dairy"],
    preferences: ["Chinese", "Thai"],
    lastVisit: "2024-08-20",
    bringsGiftsForKids: true,
    preferredGiftTypes: ["educational", "crafts"],
    giftBudgetRange: "under_25",
    giftNotes: "Prefers educational puzzles and art supplies",
    winePreferences: "Enjoys light white wines and sake",
    foodPreferences: "Loves authentic Chinese cuisine and spicy Thai food"
  },
  {
    id: 5,
    name: "Lisa Chen",
    email: "lisa.chen@email.com",
    avatar: "https://images.unsplash.com/photo-1597621969117-1a305d3e0c68",
    avatarAlt: "Asian woman with short black hair in professional blazer",
    familyId: "family_2",
    familyName: "Chen Family",
    dietaryRestrictions: ["Pescatarian"],
    allergies: [],
    preferences: ["Japanese", "Seafood"],
    lastVisit: "2024-08-20",
    bringsGiftsForKids: true,
    preferredGiftTypes: ["books", "crafts"],
    giftBudgetRange: "25_50",
    giftNotes: "Enjoys bringing Japanese-style craft kits and story books",
    winePreferences: "Prefers sake and light wines, especially with seafood",
    foodPreferences: "Expert in Japanese cuisine, loves fresh seafood preparations"
  },
  {
    id: 6,
    name: "Robert Williams",
    email: "robert.williams@email.com",
    avatar: "https://images.unsplash.com/photo-1631894862790-b8bf1598eb85",
    avatarAlt: "Middle-aged man with beard and friendly expression in casual shirt",
    familyId: null,
    dietaryRestrictions: ["Keto"],
    allergies: [],
    preferences: ["Steakhouse", "Low-carb"],
    lastVisit: "2024-09-28",
    bringsGiftsForKids: false,
    preferredGiftTypes: [],
    giftBudgetRange: "",
    giftNotes: "",
    winePreferences: "Wine enthusiast, particularly enjoys full-bodied reds",
    foodPreferences: "Follows keto diet, loves high-quality steaks and low-carb options"
  }];

  const filteredGuests = mockGuests?.filter((guest) =>
    guest?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    guest?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  const familyGroups = mockGuests?.reduce((groups, guest) => {
    if (guest?.familyId) {
      if (!groups?.[guest?.familyId]) {
        groups[guest.familyId] = {
          id: guest?.familyId,
          name: guest?.familyName,
          members: []
        };
      }
      groups?.[guest?.familyId]?.members?.push(guest);
    }
    return groups;
  }, {});

  const isGuestSelected = (guestId) => selectedGuests?.includes(guestId);

  const isFamilyFullySelected = (familyId) => {
    const family = familyGroups?.[familyId];
    if (!family) return false;
    return family?.members?.every((member) => selectedGuests?.includes(member?.id));
  };

  const getDietaryChips = (guest) => {
    const allRestrictions = [
      ...guest?.dietaryRestrictions,
      ...guest?.allergies?.map((allergy) => `Allergic to ${allergy}`)
    ];
    return allRestrictions;
  };

  const getSelectedGuestsWithGifts = () => {
    return mockGuests?.filter(guest => 
      selectedGuests?.includes(guest?.id) && guest?.bringsGiftsForKids
    );
  };

  const handleAddNewGuest = (guestData) => {
    // Generate a new ID for the guest
    const newId = Math.max(...mockGuests?.map(g => g?.id)) + 1;
    
    // Create new guest object with proper structure including new fields
    const newGuest = {
      id: newId,
      name: guestData?.name,
      email: guestData?.email,
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e",
      avatarAlt: `Portrait photo of ${guestData?.name}`,
      familyId: guestData?.familyGroup ? `family_${newId}` : null,
      familyName: guestData?.familyGroup || null,
      dietaryRestrictions: guestData?.dietaryRestrictions?.filter(r => r?.type === 'preference')?.map(r => r?.name) || [],
      allergies: guestData?.dietaryRestrictions?.filter(r => r?.type === 'allergy')?.map(r => r?.name) || [],
      preferences: [],
      lastVisit: null,
      phone: guestData?.phone || '',
      notes: guestData?.notes || '',
      // Add new gift management fields
      bringsGiftsForKids: guestData?.bringsGiftsForKids || false,
      preferredGiftTypes: guestData?.preferredGiftTypes || [],
      giftBudgetRange: guestData?.giftBudgetRange || '',
      giftNotes: guestData?.giftNotes || '',
      // Add new preference fields
      winePreferences: guestData?.winePreferences || '',
      foodPreferences: guestData?.foodPreferences || ''
    };
    
    // Add to mock data (in real app, this would be an API call)
    mockGuests?.push(newGuest);
    
    // Automatically select the new guest
    onGuestToggle(newId);
    
    // Close modal
    setIsAddGuestModalOpen(false);
    
    // Clear search to show the new guest
    setSearchTerm('');
  };

  const GiftIndicator = ({ guest }) => {
    if (!guest?.bringsGiftsForKids) return null;
    
    return (
      <div className="flex items-center space-x-1 text-xs text-success-foreground bg-success/10 px-2 py-1 rounded-full">
        <Icon name="Gift" size={12} />
        <span>Brings gifts</span>
      </div>
    );
  };

  const GuestCard = ({ guest }) => (
    <div className={`p-4 border rounded-lg transition-warm cursor-pointer ${
      isGuestSelected(guest?.id)
        ? 'border-primary bg-primary/5' :'border-border bg-card hover:border-primary/50'
    }`} onClick={() => onGuestToggle(guest?.id)}>
      <div className="flex items-start space-x-3">
        <div className="relative">
          <Image
            src={guest?.avatar}
            alt={guest?.avatarAlt}
            className="w-12 h-12 rounded-full object-cover"
          />
          {isGuestSelected(guest?.id) && (
            <div className="absolute -top-1 -right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
              <Icon name="Check" size={12} color="white" />
            </div>
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-foreground truncate">{guest?.name}</h4>
          <p className="text-sm text-muted-foreground truncate">{guest?.email}</p>
          
          {guest?.lastVisit && (
            <p className="text-xs text-muted-foreground mt-1">
              Last visit: {new Date(guest.lastVisit)?.toLocaleDateString()}
            </p>
          )}
          
          {/* Gift Indicator */}
          <div className="flex items-center space-x-2 mt-2">
            <GiftIndicator guest={guest} />
          </div>
          
          {getDietaryChips(guest)?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {getDietaryChips(guest)?.slice(0, 3)?.map((restriction, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-warning/10 text-warning-foreground"
                >
                  {restriction}
                </span>
              ))}
              {getDietaryChips(guest)?.length > 3 && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-muted text-muted-foreground">
                  +{getDietaryChips(guest)?.length - 3} more
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const FamilyGroupCard = ({ family }) => (
    <div className={`p-4 border rounded-lg transition-warm ${
      isFamilyFullySelected(family?.id)
        ? 'border-primary bg-primary/5' :'border-border bg-card'
    }`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <Icon name="Users" size={16} className="text-muted-foreground" />
          <h4 className="font-medium text-foreground">{family?.name}</h4>
          <span className="text-sm text-muted-foreground">({family?.members?.length} members)</span>
        </div>
        
        <Button
          variant="outline"
          size="sm"
          onClick={() => onFamilyToggle(family?.id)}
        >
          {isFamilyFullySelected(family?.id) ? 'Remove All' : 'Add All'}
        </Button>
      </div>
      
      <div className="space-y-2">
        {family?.members?.map((member) => (
          <GuestCard key={member?.id} guest={member} />
        ))}
      </div>
    </div>
  );

  // Gift Checklist Component
  const GiftChecklist = () => {
    const guestsWithGifts = getSelectedGuestsWithGifts();
    
    if (guestsWithGifts?.length === 0) return null;

    return (
      <div className="bg-success/5 border border-success/20 rounded-lg p-4 mt-4">
        <div className="flex items-center space-x-2 mb-3">
          <Icon name="Gift" size={16} className="text-success" />
          <h4 className="font-medium text-success-foreground">Gift Checklist for Kids</h4>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowGiftChecklist(!showGiftChecklist)}
          >
            <Icon name={showGiftChecklist ? "ChevronUp" : "ChevronDown"} size={16} />
          </Button>
        </div>
        
        {showGiftChecklist && (
          <div className="space-y-3">
            {guestsWithGifts?.map((guest) => (
              <div key={guest?.id} className="flex items-start space-x-3 p-3 bg-card rounded-lg border border-border">
                <Checkbox
                  label=""
                 
                  onChange={() => {}}
                />
                <div className="flex-1">
                  <p className="font-medium text-foreground">{guest?.name}</p>
                  <p className="text-sm text-muted-foreground">
                    Bringing: {guest?.preferredGiftTypes?.map(type => 
                      type?.charAt(0)?.toUpperCase() + type?.slice(1)
                    )?.join(', ') || 'Gifts'}
                  </p>
                  {guest?.giftNotes && (
                    <p className="text-xs text-muted-foreground mt-1">{guest?.giftNotes}</p>
                  )}
                </div>
              </div>
            ))}
            <div className="text-xs text-muted-foreground mt-2">
              💡 Remember to confirm gift details when sending invitations
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Select Guests</h3>
        <div className="flex items-center space-x-2">
          <Checkbox
            label="Show family groups"
            checked={showFamilyGroups}
            onChange={(e) => setShowFamilyGroups(e?.target?.checked)}
          />
        </div>
      </div>
      
      <div className="mb-4">
        <Input
          type="search"
          placeholder="Search guests by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e?.target?.value)}
        />
      </div>

      {selectedGuests?.length > 0 && (
        <div className="mb-4 p-3 bg-primary/5 rounded-lg border border-primary/20">
          <div className="flex items-center space-x-2">
            <Icon name="Users" size={16} className="text-primary" />
            <span className="text-sm font-medium text-primary">
              {selectedGuests?.length} guest{selectedGuests?.length !== 1 ? 's' : ''} selected
            </span>
          </div>
        </div>
      )}

      {/* Gift Checklist */}
      <GiftChecklist />

      <div className="space-y-3 max-h-96 overflow-y-auto">
        {showFamilyGroups ? (
          <>
            {Object.values(familyGroups)?.map((family) => (
              <FamilyGroupCard key={family?.id} family={family} />
            ))}
            
            <div className="border-t border-border pt-3 mt-3">
              <h5 className="text-sm font-medium text-muted-foreground mb-2">Individual Guests</h5>
              {filteredGuests?.filter((guest) => !guest?.familyId)?.map((guest) => (
                <GuestCard key={guest?.id} guest={guest} />
              ))}
            </div>
          </>
        ) : (
          filteredGuests?.map((guest) => (
            <GuestCard key={guest?.id} guest={guest} />
          ))
        )}
      </div>

      {filteredGuests?.length === 0 && (
        <div className="text-center py-8">
          <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-2" />
          <p className="text-muted-foreground mb-4">No guests found matching your search.</p>
          {searchTerm && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Can't find "{searchTerm}"? Create a new guest instead.
              </p>
              <Button
                variant="default"
                onClick={() => setIsAddGuestModalOpen(true)}
                iconName="UserPlus"
                iconPosition="left"
              >
                Create New Guest
              </Button>
            </div>
          )}
        </div>
      )}

      <AddGuestModal
        isOpen={isAddGuestModalOpen}
        onClose={() => setIsAddGuestModalOpen(false)}
        onSave={handleAddNewGuest}
        existingGuest={null}
      />
    </div>
  );
};

export default GuestSelectionPanel;